import React from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import trackClick from 'js/utils/trackClick';

import {
  impressionExtra,
  resolveNavImpactStats,
} from '../autotest/AutotestNavImpactStats';
import type { StatLayout, StatStyle } from '../autotest/AutotestNavImpactStats';
import type { ImpactStats } from '../types';
import ImpactStatTile from './ImpactStatTile';
import TrendAdornment from './ornaments/TrendAdornment';
import type { TrendVariant } from './ornaments/TrendAdornment';

export const NAV_IMPACT_STRIP_MIN_WIDTH = 140;

type NavImpactStatsProps = {
  arm: string | null | undefined;
  impactStats?: ImpactStats | null;
  isMobileNav?: boolean;
};

const StripContainer = styled.div<{ isMobileNav?: boolean }>`
  flex: 0 0 auto;
  width: ${NAV_IMPACT_STRIP_MIN_WIDTH}px;
  min-width: ${NAV_IMPACT_STRIP_MIN_WIDTH}px;
  height: 48px;

  &.isMobileNav {
    height: 40px;
  }
`;

const StripWatcher = styled(ImpressionWatcher)`
  width: 100%;
  height: 100%;
`;

const TileRow = styled.div<{ statLayout: StatLayout }>`
  display: flex;
  width: 100%;
  height: 100%;

  &.statLayout-stacked {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  &.statLayout-inline {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
`;

const CellDivider = styled.span`
  flex: 0 0 auto;
  width: 1px;
  height: 24px;
  margin: 0 1px;
  background: var(--background-grey, #dddde2);
`;

const TREND_VARIANTS: Partial<Record<StatStyle, TrendVariant>> = {
  delta_chip: 'chip',
  trend_icon: 'icon',
};

function trendAdornment(
  statStyle: StatStyle,
  pct: number | null,
): React.ReactNode {
  const variant = TREND_VARIANTS[statStyle];
  if (variant == null || pct == null) {
    return undefined;
  }
  return <TrendAdornment variant={variant} pct={pct} />;
}

const NavImpactStats = ({
  arm,
  impactStats,
  isMobileNav = false,
}: NavImpactStatsProps) => {
  const surfaces = resolveNavImpactStats(arm);
  if (surfaces.statStyle === 'none') {
    return null;
  }

  const citations = impactStats?.citations ?? null;
  const paperViews = impactStats?.paperViews30d ?? null;
  const hasData = citations != null || paperViews != null;
  const bucket = arm ?? 'control';

  return (
    <StripContainer data-testid="nav-impact-stats" isMobileNav={isMobileNav}>
      {hasData && (
        <StripWatcher
          source="nav_impact_stats.strip"
          extra={impressionExtra(arm)}
        >
          <TileRow statLayout={surfaces.statLayout}>
            <ImpactStatTile
              count={citations}
              label="Citations"
              layout={surfaces.statLayout}
              adornment={trendAdornment(
                surfaces.statStyle,
                impactStats?.citationsTrendPct ?? null,
              )}
              href={routes.citations_url({
                from_navbar: true,
                trigger: 'nav_impact_stats',
              })}
              onClick={() =>
                trackClick({ source: 'nav-bar--impact-citations', bucket })
              }
            />
            {surfaces.statLayout === 'stacked' &&
              citations != null &&
              paperViews != null && (
                <CellDivider data-testid="impact-cell-divider" />
              )}
            <ImpactStatTile
              count={paperViews}
              label="Paper views"
              layout={surfaces.statLayout}
              adornment={trendAdornment(
                surfaces.statStyle,
                impactStats?.paperViewsTrendPct ?? null,
              )}
              href="/analytics"
              onClick={() =>
                trackClick({ source: 'nav-bar--impact-paper-views', bucket })
              }
            />
          </TileRow>
        </StripWatcher>
      )}
    </StripContainer>
  );
};

export default NavImpactStats;
